/* Created by Interactive 63 - https://interactive63.com */
/**
 * Transport controls in the top bar: play and pause, stop, loop, speed and the current time,
 * which can be clicked to type a time.
 */
import type { PlaybackController, PlaybackRate } from '../playback';
import { formatTime, parseTime } from '../time-format';
import { Component } from '../ui/component';
import { announce } from '../ui/announcer';
import { h } from '../ui/dom';

const RATES: PlaybackRate[] = [0.25, 0.5, 1, 2];

export class Transport extends Component<PlaybackController> {
  /** Creates the container for the controls. */
  protected override createRoot(): HTMLElement {
    return h('div', { class: 'transport', role: 'group', 'aria-label': 'Playback' });
  }

  /** Builds the controls and keeps them in step with playback and the scene duration. */
  protected override init(): void {
    const playback = this.props;
    const play = h(
      'button',
      { type: 'button', class: 'transport-play', title: 'Play (Space)' },
      'Play',
    );
    const stop = h('button', { type: 'button', title: 'Stop and return to the start' }, 'Stop');
    const loop = h('button', { type: 'button', 'aria-pressed': 'true', title: 'Loop' }, 'Loop');
    const speed = h(
      'select',
      { 'aria-label': 'Playback speed', title: 'Playback speed' },
      ...RATES.map((rate) => h('option', { value: rate, selected: rate === 1 }, `${rate}×`)),
    );
    const current = h('button', {
      type: 'button',
      class: 'time-current',
      title: 'Click to type a time',
    });
    const total = h('span', { class: 'time-total' });
    const pass = h('span', {
      class: 'pass-badge',
      hidden: true,
      title: 'Plays-once tracks are holding their end pose, as they will in a looping export.',
    });

    play.addEventListener('click', () => {
      if (playback.isPlaying) playback.pause();
      else playback.play();
    });
    stop.addEventListener('click', () => {
      playback.stop();
    });
    loop.addEventListener('click', () => {
      const on = loop.getAttribute('aria-pressed') !== 'true';
      loop.setAttribute('aria-pressed', String(on));
      playback.setLoop(on);
    });
    speed.addEventListener('change', () => {
      playback.setRate(Number(speed.value) as PlaybackRate);
    });
    current.addEventListener('click', () => {
      this.editTime(current);
    });

    this.el.append(
      play,
      stop,
      loop,
      speed,
      h('span', { class: 'time-display' }, current, h('span', { class: 'time-sep' }, ' / '), total),
      pass,
    );

    this.addCleanup(
      playback.onTick((ms) => {
        current.textContent = formatTime(ms);
        pass.hidden = playback.pass === 0;
        pass.textContent = `Pass ${playback.pass + 1}`;
      }),
    );
    this.select(
      (s) => s.editor.isPlaying,
      (isPlaying, previous) => {
        if (isPlaying !== previous) announce(isPlaying ? 'Playing' : 'Paused', { key: 'playback' });
        play.textContent = isPlaying ? 'Pause' : 'Play';
        play.title = isPlaying ? 'Pause (Space)' : 'Play (Space)';
      },
      { immediate: true },
    );
    this.select(
      (s) => s.project.scene.duration,
      (duration) => {
        total.textContent = formatTime(duration);
      },
      { immediate: true },
    );
    current.textContent = formatTime(playback.currentTime);
  }

  /** Swaps the time readout for a text field; Enter jumps to the typed time. */
  private editTime(button: HTMLElement): void {
    const input = h('input', {
      class: 'time-input',
      value: button.textContent,
      'aria-label': 'Go to time',
    });
    button.replaceWith(input);
    input.focus();
    input.select();
    let done = false;
    /** Restores the readout, seeking to the typed time when requested. */
    const finish = (apply: boolean): void => {
      if (done) return;
      done = true;
      input.replaceWith(button);
      const ms = parseTime(input.value);
      if (apply && ms !== null) this.props.seek(ms);
    };
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => {
      finish(false);
    });
  }
}
